/**
 * ROLE-LENS-DESIGN-SYSTEM-V1 -- RecordHeader: the generic record identity
 * strip (golden-record display name, UMR, status chip, clinic label, CRM
 * stage), with the audience prop deciding WHICH facets render.
 *
 * SOURCE SCREENS (extraction register):
 * - PRIMARY: the Lead 360 record header -- the screen-local `RecordHeader`
 *   in frontend/src/dashboard/record/RecordHeaderBand.tsx (RECORD-360-
 *   WORKSPACE-V1; R-30 fix: labeled FieldGrid facts, never comma-spliced
 *   prose). Proof status: the record band is PROVEN live 2026-07-11
 *   (WALK 5 + WALK 6, #252 -- golden source); the V7-P4A header retrofit
 *   steps ride WALK 15, LIVE-RUN PENDING. That screen-local component stays
 *   UNTOUCHED and keeps its name -- it is the extraction SOURCE bound to
 *   the lead current-truth contract, not a competitor; adoption packages
 *   later fold it onto this primitive.
 * - SECOND CONSUMER OF THE GRAMMAR: the Patient 360 header
 *   (frontend/src/dashboard/patients/PatientRecordPage.tsx, `.record360-
 *   header` + patient360-umr). Proof status: WALK 21 PASSED 2026-07-15
 *   (golden source); WALK 18 steps LIVE-RUN PENDING.
 *
 * AUDIENCE RULE (fail closed, per ui/role-lens.ts): the display name + the
 * eyebrow are the identity minimum -- a caller holding the record lawfully
 * holds its name. Every OTHER facet (UMR, status chip, clinic, stage,
 * extra facts, actions) renders only for a KNOWN STAFF audience by default
 * (the shared dashboard is a staff surface; the patient role does not
 * inherit staff facets). An unknown or absent audience renders the name
 * ONLY. Callers may narrow facet visibility further via `factsAudiences`;
 * widening beyond staff is not offered by this primitive.
 *
 * ACTIONS SLOT: LAWFUL-ACTIONS ONLY -- callers pass role-held doors only
 * (LES-016 links are role-shaped or absent; the slot inherits the source
 * screen's Scope 2b contract) and the slot renders only for a known staff
 * audience.
 *
 * CLINICAL WIRE LAW (CLAUDE.md "Clinical visibility"): presentation, never
 * the security boundary. This strip renders ADMINISTRATIVE identity facets
 * from an already-lawful payload; clinical content never belongs in it, and
 * server SELECT fences remain the authority.
 */

import type { ReactNode } from 'react';
import FieldGrid, { type FieldGridField } from './FieldGrid';
import StatusChip, { type StatusChipProps } from './StatusChip';
import { audienceAllows, STAFF_AUDIENCES, type RoleLensAudience } from './role-lens';

/** The stage micro-label (RecordHeaderBand STAGE DISPLAY RULE, verbatim). */
export const RECORD_HEADER_STAGE_LABEL = 'CRM stage';
/** The UMR facet label (RecordHeaderBand/Patient 360 'Patient ID' idiom). */
export const RECORD_HEADER_UMR_LABEL = 'Patient ID';
/**
 * THE clinic facet on a record header answers ONE question: where did this
 * PATIENT ENTER THE NETWORK. It is a historical, immutable property of the
 * record (`patients.registered_clinic_id`, insert-only).
 *
 * It is NOT the lens ("which clinic am I looking through"), and it is NOT the
 * treating clinic of any episode ("where did this visit happen") -- those are
 * different questions with different answers, and this header previously
 * printed the bare word "Clinic" for the first while the page printed the
 * same bare word for the third. The prop is named for the question so a
 * future caller cannot pass a lens value into it without noticing.
 */
export const RECORD_HEADER_REGISTERED_AT_LABEL = 'Registered at';

export interface RecordHeaderStatus {
  readonly domain: StatusChipProps['domain'];
  readonly status: string;
  /** Caller-resolved label for contextual statuses (StatusChip contract). */
  readonly label?: string | null;
}

export interface RecordHeaderProps {
  /** The viewer's audience (unknown/absent -> identity minimum only). */
  readonly audience: RoleLensAudience | string | null | undefined;
  /** Golden-record display name or the honest ref (e.g. "Lead #36"). */
  readonly name: string;
  /** Small record-kind eyebrow (e.g. "Lead record" / "Patient record"). */
  readonly eyebrow?: string;
  /** AdoraCare UMR when linked (renders as the labeled 'Patient ID'). */
  readonly umr?: string | null;
  /** The record's headline status chip (THE StatusChip canon). */
  readonly status?: RecordHeaderStatus | null;
  /** Where the patient ENTERED THE NETWORK -- never the lens, never an
   *  episode's treating clinic. See RECORD_HEADER_REGISTERED_AT_LABEL. */
  readonly registeredClinicLabel?: string | null;
  /** The caller-resolved CRM stage label (the rail owns stage wording). */
  readonly stageLabel?: string | null;
  /** Additional labeled facts (already lawful for this audience). */
  readonly facts?: readonly FieldGridField[];
  /** Already-authorized record context; inherits the staff facet gate. */
  readonly summary?: ReactNode;
  /** Audiences for the gated facets (default: staff). Narrowing only. */
  readonly factsAudiences?: readonly RoleLensAudience[];
  /** LAWFUL-ACTIONS ONLY: role-held doors, staff audiences only. */
  readonly actions?: ReactNode;
  /** Identity avatar slot (e.g. the PHI-safe initials chip). */
  readonly avatar?: ReactNode;
  readonly testId?: string;
}

export default function RecordHeader({
  audience,
  name,
  eyebrow,
  umr,
  status,
  registeredClinicLabel,
  stageLabel,
  facts,
  summary,
  factsAudiences,
  actions,
  avatar,
  testId,
}: RecordHeaderProps): JSX.Element {
  const showFacets = audienceAllows(audience, factsAudiences ?? STAFF_AUDIENCES);
  const fields: FieldGridField[] = showFacets
    ? [
        ...(umr !== undefined && umr !== null && umr !== ''
          ? [
              {
                label: RECORD_HEADER_UMR_LABEL,
                value: <span data-testid="recordheader-umr">{umr}</span>,
              },
            ]
          : []),
        ...(registeredClinicLabel !== undefined &&
        registeredClinicLabel !== null &&
        registeredClinicLabel !== ''
          ? [
              {
                label: RECORD_HEADER_REGISTERED_AT_LABEL,
                value: registeredClinicLabel,
              },
            ]
          : []),
        ...(stageLabel !== undefined && stageLabel !== null && stageLabel !== ''
          ? [
              {
                label: RECORD_HEADER_STAGE_LABEL,
                value: <span data-testid="recordheader-stage">{stageLabel}</span>,
              },
            ]
          : []),
        ...(facts ?? []),
      ]
    : [];
  return (
    <header
      className="ac-recordheader"
      data-facets={showFacets ? 'full' : 'identity-minimum'}
      data-testid={testId}
    >
      {avatar ? (
        <span className="ac-recordheader__avatar" aria-hidden="true">
          {avatar}
        </span>
      ) : null}
      <div className="ac-recordheader__identity">
        {eyebrow ? (
          <span className="ac-recordheader__eyebrow">{eyebrow}</span>
        ) : null}
        <div className="ac-recordheader__titlerow">
          <h1 className="ac-recordheader__title" data-testid="recordheader-title">
            {name}
          </h1>
          {showFacets && status ? (
            <StatusChip
              domain={status.domain}
              status={status.status}
              label={status.label}
              testId="recordheader-status"
            />
          ) : null}
        </div>
        {showFacets && summary ? summary : null}
        {fields.length > 0 ? (
          <FieldGrid
            label="Record identity facts"
            fields={fields}
            testId="recordheader-facts"
          />
        ) : null}
      </div>
      {showFacets && actions !== undefined && actions !== null ? (
        <div className="ac-recordheader__actions" data-testid="recordheader-actions">
          {actions}
        </div>
      ) : null}
    </header>
  );
}
